/**
 * @file Agregateur central des bonus : final = (base + somme des add) x produit des mult, avec cache invalide par ModifiersDirty.
 */
import { Decimal } from '@shared/bignum';
import { STAT_IDS } from '@shared/enums';
import type { StatId } from '@shared/enums';
import type { GameConfig } from '@config/index';
import { matchesFilter } from './Modifier';
import type { Modifier, ModifierSource, StatQuery } from './Modifier';

/** Valeur de base de chaque stat (avant modificateurs). */
export const statBases = (cfg: GameConfig): Record<StatId, number> => {
  const base = Object.fromEntries(STAT_IDS.map((s) => [s, 1])) as Record<StatId, number>;
  base.critChance = cfg.balance.combat.baseCritChance;
  base.critDamage = cfg.balance.combat.baseCritDamage;
  base.moveSpeed = cfg.balance.combat.baseMoveSpeed;
  base.offlineHours = cfg.balance.offline.baseHoursCap;
  base.offlineRate = cfg.balance.offline.efficiency;
  return base;
};

export interface StatBreakdown {
  stat: StatId;
  base: number;
  add: Decimal;
  mult: Decimal;
  total: Decimal;
  modifiers: Modifier[];
}

export class StatAggregator {
  private readonly sources: ModifierSource[] = [];
  private mods: Modifier[] = [];
  private dirty = true;
  private readonly cache = new Map<string, Decimal>();
  private readonly bases: Record<StatId, number>;
  /** Incremente a chaque reconstruction : permet aux caches derives (WeaponStats) de savoir s'ils sont perimes. */
  version = 0;

  constructor(cfg: GameConfig) {
    this.bases = statBases(cfg);
  }

  addSource(src: ModifierSource): void {
    this.sources.push(src);
    this.dirty = true;
  }

  invalidate(): void {
    this.dirty = true;
  }

  private rebuild(): void {
    const list: Modifier[] = [];
    const sink = { push: (m: Modifier) => void list.push(m) };
    for (const s of this.sources) s.collect(sink);
    this.mods = list;
    this.cache.clear();
    this.dirty = false;
    this.version++;
  }

  private ensure(): void {
    if (this.dirty) this.rebuild();
  }

  /** Valeur finale d'une stat dans un contexte (mode, comportement d'arme, slot). */
  get(stat: StatId, q: StatQuery = {}): Decimal {
    this.ensure();
    const key = `${stat}|${q.mode ?? ''}|${q.behavior ?? ''}|${q.slot ?? ''}|${q.excludeBuffs ? 1 : 0}`;
    const hit = this.cache.get(key);
    if (hit) return hit;
    let add = new Decimal(this.bases[stat]);
    let mult = new Decimal(1);
    for (const m of this.mods) {
      if (m.stat !== stat || !matchesFilter(m.filter, q)) continue;
      if (q.excludeBuffs && m.source.startsWith('buff:')) continue;
      if (m.op === 'add') add = add.plus(m.value);
      else mult = mult.times(m.value);
    }
    const total = add.times(mult);
    this.cache.set(key, total);
    return total;
  }

  /** Raccourci flottant (stats bornees : critChance, moveSpeed, offlineHours...). */
  getNumber(stat: StatId, q: StatQuery = {}): number {
    const n = this.get(stat, q).toNumber();
    return Number.isFinite(n) ? n : Number.MAX_VALUE;
  }

  /** Detail pour l'ecran "bonus" de l'UI. */
  breakdown(stat: StatId, q: StatQuery = {}): StatBreakdown {
    this.ensure();
    const modifiers = this.mods.filter((m) => m.stat === stat && matchesFilter(m.filter, q));
    let add = new Decimal(0);
    let mult = new Decimal(1);
    for (const m of modifiers) {
      if (m.op === 'add') add = add.plus(m.value);
      else mult = mult.times(m.value);
    }
    return { stat, base: this.bases[stat], add, mult, total: add.plus(this.bases[stat]).times(mult), modifiers };
  }
}
